import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, EmptyState, Field } from "@secretaa/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router";
import { api } from "../../app/api.ts";
import { canManage, useCurrentWorkspace, useSession } from "../../app/session.ts";
import { errorMessage, isAuthError } from "../../shared/errors.ts";
import { Form, FormError, Notice, PageHeader } from "../../shared/form.tsx";
import { PendingInvitations, usePendingInvitations } from "../invite/pending-invitations.tsx";

export function SelectWorkspacePage() {
  const { t } = useTranslation();
  const session = useSession();
  const pending = usePendingInvitations();
  if (session.isPending) return <p className="p-6">{t("app.loading")}</p>;
  if (session.isError) return isAuthError(session.error) ? <Navigate to="/login" replace /> : <p className="p-6">{t("errors.generic")}</p>;
  const list = session.data.memberships;
  // Còn lời mời đang chờ thì KHÔNG tự chuyển vào workspace duy nhất — người dùng phải thấy lời mời (SAA-2026-011).
  if (list.length === 1 && pending.isPending) return <p className="p-6">{t("app.loading")}</p>;
  const hasPending = (pending.data?.length ?? 0) > 0;
  if (list.length === 1 && !hasPending) return <Navigate to={`/w/${list[0]!.workspaceId}/overview`} replace />;
  return (
    <main id="main" className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-4 p-6">
      <h1 className="text-xl font-semibold">{t("workspace.select.title")}</h1>
      <PendingInvitations />
      {list.length === 0 && !hasPending ? (
        <>
          <p>{t("workspace.select.empty")}</p>
          <p className="text-sm text-(--saa-fg-muted)">{t("workspace.select.emptyHint")}</p>
        </>
      ) : null}
      <ul className="flex flex-col gap-2">
        {list.map((m) => (
          <li key={m.workspaceId}>
            <Link className="block rounded-md border border-(--saa-border) bg-(--saa-surface) px-4 py-3 hover:bg-(--saa-muted)" to={`/w/${m.workspaceId}/overview`}>
              {m.workspaceName}
            </Link>
          </li>
        ))}
      </ul>
      <Button asChild variant="secondary">
        <Link to="/onboarding/workspace">{t("workspace.select.create")}</Link>
      </Button>
    </main>
  );
}

export function OnboardingWorkspacePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: () => api.workspaces.create(name),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: qk.me() });
      navigate(`/w/${r.workspace.id}/overview`);
    },
  });
  return (
    <main id="main" className="mx-auto flex min-h-full max-w-md flex-col justify-center p-6">
      <Card>
        <h1 className="mb-2 text-xl font-semibold">{t("workspace.onboarding.title")}</h1>
        <p className="mb-4 text-sm text-(--saa-fg-muted)">{t("workspace.onboarding.hint")}</p>
        <Form label={t("workspace.onboarding.title")} onSubmit={() => create.mutate()}>
          <FormError message={create.isError ? errorMessage(t, create.error) : null} />
          <Field label={t("workspace.onboarding.name")} required value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            {t("workspace.onboarding.submit")}
          </Button>
        </Form>
      </Card>
    </main>
  );
}

export function WorkspaceSettingsPage() {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const qc = useQueryClient();
  const ws = useQuery({ queryKey: qk.workspace(cur.tenant), queryFn: () => api.workspaces.get(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const [name, setName] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.workspaces.rename(cur.workspaceId, (name ?? "").trim(), ws.data!.workspace.version),
    onSuccess: async (r) => {
      qc.setQueryData(qk.workspace(cur.tenant), r);
      await qc.invalidateQueries({ queryKey: qk.me() });
      setName(null);
    },
  });
  if (ws.isPending) return <p>{t("app.loading")}</p>;
  if (ws.isError) return <FormError message={errorMessage(t, ws.error)} />;
  const value = name ?? ws.data.workspace.name;
  return (
    <>
      <PageHeader title={t("workspace.settings.title")} />
      <Card className="max-w-lg">
        <Form label={t("workspace.settings.title")} onSubmit={() => save.mutate()}>
          <FormError message={save.isError ? errorMessage(t, save.error) : null} />
          {save.isSuccess ? <Notice>{t("workspace.settings.saved")}</Notice> : null}
          <Field label={t("workspace.settings.name")} value={value} disabled={!canManage(cur.role)} onChange={(e) => setName(e.target.value)} />
          {canManage(cur.role) ? (
            <Button type="submit" disabled={save.isPending || !value.trim() || value === ws.data.workspace.name}>
              {t("common.save")}
            </Button>
          ) : null}
        </Form>
      </Card>
      <OrphanGrantsCard />
    </>
  );
}

/**
 * Đối soát grant mồ côi (SAA-2026-012 D1.2): « Kiểm tra » chỉ xem; « Dọn » kiểm lại phía server rồi thu hồi. Hiện với mọi
 * vai trò (không ẩn theo vai trò) — member bấm thì server trả 403 và câu lỗi nói rõ.
 */
function OrphanGrantsCard() {
  const { t, i18n } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const check = useMutation({ mutationFn: () => api.projectGrants.reconcile(cur.workspaceId, false) });
  const clean = useMutation({ mutationFn: () => api.projectGrants.reconcile(cur.workspaceId, true), onSuccess: () => check.reset() });
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const report = check.data;
  const orphanGrants = report?.orphans.reduce((n, o) => n + o.userIds.length, 0) ?? 0;
  return (
    <Card className="mt-6 flex max-w-lg flex-col gap-3">
      <h2 className="font-semibold">{t("workspace.settings.grants.title")}</h2>
      <p className="text-sm text-(--saa-fg-muted)">{t("workspace.settings.grants.description")}</p>
      <FormError message={check.isError ? errorMessage(t, check.error) : clean.isError ? errorMessage(t, clean.error) : null} />
      {clean.isSuccess ? <Notice>{t("workspace.settings.grants.revoked", { count: clean.data.revoked })}</Notice> : null}
      {report ? (
        <div className="flex flex-col gap-2 text-sm">
          <p>{t("workspace.settings.grants.summary", { checked: report.checkedProjects, orphans: report.orphans.length, skipped: report.skipped.length })}</p>
          {report.orphans.length === 0 ? (
            <p>{t("workspace.settings.grants.none")}</p>
          ) : (
            <ul className="list-disc pl-5">
              {report.orphans.map((o) => (
                <li key={o.projectId} className="font-mono text-xs">
                  {t("workspace.settings.grants.orphan", { id: o.projectId, count: o.userIds.length, since: fmt.format(new Date(o.oldestGrantAt)) })}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" disabled={check.isPending} onClick={() => (clean.reset(), check.mutate())}>
          {t("workspace.settings.grants.check")}
        </Button>
        {report && report.orphans.length > 0 ? (
          <Button variant="danger" disabled={clean.isPending} onClick={() => window.confirm(t("workspace.settings.grants.cleanConfirm", { count: orphanGrants })) && clean.mutate()}>
            {t("workspace.settings.grants.clean")}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

export function NoWorkspaceState() {
  const { t } = useTranslation();
  return <EmptyState title={t("workspace.select.empty")} />;
}
